import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { useRediscoveryApplications } from 'talent_matching/talent_rediscovery/hooks/use_rediscovery_applications';
import { useRediscoveryColumns } from 'talent_matching/talent_rediscovery/components/table/rediscovery_columns';
import BaseDataTable from 'talent_matching/components/table/base_data_table';
import NoOverflowFullWidth from 'talent_matching/components/shared/no_overflow_full_width';
import { TalentRediscoveryFilters } from 'talent_search/shared/types';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { useLocalStorage } from 'react-use';
import {
  REDISCOVERY_DEFAULT_COLUMN_VISIBILITY_MODEL,
  REDISCOVERY_ACTIONS_COLUMN_FIELD,
} from './rediscovery_column_visibility_defaults';
import RediscoveryNoResultsOverlay from './rediscovery_no_results_overlay';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import { useUserContext, TABS } from 'talent_matching/contexts/user_context';

type RediscoveryTableWrapperProps = {
  onTotalRowsChange?: (totalRows: number) => void;
  columnVisibilityModel?: GridColumnVisibilityModel;
  setColumnVisibilityModel?: (model: GridColumnVisibilityModel) => void;
  hasAnyFilters?: boolean;
  onClearAll?: () => void;
};

const useColumnVisibility = (
  initialColumns: GridColumnVisibilityModel
): [
  GridColumnVisibilityModel | undefined,
  (model: GridColumnVisibilityModel) => void
] => {
  const [columnVisibilityModel, setStoredColumnVisibilityModel] =
    useLocalStorage<GridColumnVisibilityModel>(
      'talent-rediscovery-column-visibility',
      initialColumns
    );

  const normalizedColumnVisibilityModel = useMemo(() => {
    if (!columnVisibilityModel) {
      return columnVisibilityModel;
    }

    return {
      ...initialColumns,
      ...columnVisibilityModel,
      fullName: true,
      [REDISCOVERY_ACTIONS_COLUMN_FIELD]: true,
    };
  }, [columnVisibilityModel, initialColumns]);

  const setColumnVisibilityModel = (model: GridColumnVisibilityModel) => {
    setStoredColumnVisibilityModel({
      ...initialColumns,
      ...model,
      fullName: true,
      [REDISCOVERY_ACTIONS_COLUMN_FIELD]: true,
    });
  };

  return [normalizedColumnVisibilityModel, setColumnVisibilityModel];
};

const RediscoveryTableWrapper = ({
  onTotalRowsChange,
  columnVisibilityModel: columnVisibilityModelProp,
  setColumnVisibilityModel: setColumnVisibilityModelProp,
  hasAnyFilters = false,
  onClearAll,
}: RediscoveryTableWrapperProps) => {
  const {
    paths: { rediscoveryApplicationsPath },
  } = useRediscoveryTabContext();
  const { activeTab, userDateFormat } = useUserContext();
  const isActive = activeTab === TABS.REDISCOVERY;
  // Track when tab becomes active to force table remount (fixes column header sizing)
  const [mountKey, setMountKey] = useState(0);
  const previousIsActive = useRef(isActive);

  useEffect(() => {
    if (!previousIsActive.current && isActive) {
      // Tab just became active - increment key to force remount
      setMountKey((k) => k + 1);
    }
    previousIsActive.current = isActive;
  }, [isActive]);

  const { watch, getValues, setValue } =
    useFormContext<TalentRediscoveryFilters>();
  const rowsPerPage = getValues('perPage') || 50;
  const currentPage = watch('page') || 1;
  const filterNames = Object.keys(getValues());
  const chosenFilterNames = filterNames.filter(
    (name) => name !== 'page' && name !== 'excludedIds'
  ) as (keyof TalentRediscoveryFilters)[];
  const chosenFilters = watch(chosenFilterNames);
  const jsonChosenFilters = JSON.stringify(chosenFilters);

  const onPageChange = (newPage: number) => setValue('page', newPage + 1);

  const {
    data: tableData,
    isLoading,
    isFetching,
    isPlaceholderData,
  } = useRediscoveryApplications(rediscoveryApplicationsPath as string);

  const rows = tableData?.rows || [];
  const totalRows = tableData?.totalRows || 0;

  // Notify parent of total rows change
  useEffect(() => {
    onTotalRowsChange?.(totalRows);
  }, [totalRows, onTotalRowsChange]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setTimeout(() => setValue('page', 1), 0);
  }, [jsonChosenFilters, setValue]);

  // Reset to page 1 if current page is out of bounds
  useEffect(() => {
    if (totalRows < (currentPage - 1) * rowsPerPage) {
      setTimeout(() => setValue('page', 1), 0);
    }
  }, [currentPage, rowsPerPage, totalRows, setValue]);

  const tableLoading = isLoading || isFetching || isPlaceholderData;

  const columns = useRediscoveryColumns({ userDateFormat });

  // Use internal column visibility if not provided via props
  const [internalColumnVisibility, setInternalColumnVisibility] =
    useColumnVisibility(REDISCOVERY_DEFAULT_COLUMN_VISIBILITY_MODEL);

  const columnVisibilityModel =
    columnVisibilityModelProp ?? internalColumnVisibility;
  const setColumnVisibilityModel =
    setColumnVisibilityModelProp ?? setInternalColumnVisibility;

  return (
    <NoOverflowFullWidth>
      <BaseDataTable
        key={mountKey}
        rows={rows}
        columns={columns}
        totalRows={totalRows}
        loading={tableLoading}
        rowsPerPage={rowsPerPage}
        currentPage={currentPage}
        onPageChange={onPageChange}
        columnVisibilityModel={
          columnVisibilityModel as GridColumnVisibilityModel
        }
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        slots={{
          noRowsOverlay: () => (
            <RediscoveryNoResultsOverlay
              userDateFormat={userDateFormat}
              hasAnyFilters={hasAnyFilters}
              onClearAll={onClearAll || (() => undefined)}
            />
          ),
        }}
      />
    </NoOverflowFullWidth>
  );
};

export default RediscoveryTableWrapper;
